// @ts-nocheck
import { parseCommandByte } from './ProtocolHeader';
import {
  LEGACY_SUPPORTED_REPORT_RATES_HZ,
  decodeReportRateByte,
  pollingRateLevelToHz,
  pollingRateTableToHz,
  resolveReportRateHz,
} from './PollingRates';
import {
  applyEffectSwitchToBuffer,
  parseLightingData,
} from './LightingProtocol';
import {
  V4_TYPE3_OFFSETS,
  V4_TYPE3_MIN_LENGTH,
  V4_TYPE3_CUSTOM_RATE_MIN_LENGTH,
} from './PerformanceType3';
import {
  parseRtGearSample,
  parseRtMonitorSample,
} from './RtMonitor';
import {
  MACRO_EVENT_TYPE_MOUSE_MOVE,
  MACRO_EVENT_TYPE_NAMES,
  MACRO_MOUSE_MOVE_DELAY_MS,
  decodeMacroAxisValue,
  decodeMacroMouseMove,
  encodeMacroMouseMove,
  isMacroAxisEventType,
} from './MacroEvents';
/**
 * 设备数据存储类
 * 用于存储和管理设备的所有配置和状态数据
 */
class DeviceDataStore {
  constructor() {
    // 未探测到版本时按当前已支持的最老协议处理。后续新增协议只需扩展
    // isLegacyProtocol() 及对应解析分支，不必改动调用方。
    this.protocolVersion = null;
    this.data = {
      // 设备基本信息
      deviceInfo: null,
      
      // 设备配置数据（命令0的响应）
      deviceConfig: null,
      
      // 通告信息（命令1的响应）
      informationData: null,
      
      // 鼠标按键信息（命令3的响应）
      mouseButtonData: null,
      
      // 设备性能数据（命令4的响应）
      // 分别存储不同类型的性能数据
      performanceData: {
        type1: null,  // 传感器设置
        type3: null   // 系统和按键设置
      },
      
      // 设备颜色数据（命令5的响应）
      colorData: null,
      
      // 宏数据（命令7的响应）
      macroData: null,

      // 4.0 RT 实时采样（只保留最新一包，曲线缓冲在界面里）
      rtMonitor: null,

      // 4.0 RT 档位采样（0xF2，档位界面按下动画用）
      rtGear: null,

      // 原始响应数据历史
      rawDataHistory: []
    };
    
    this.listeners = new Map();
    this.maxHistorySize = 50;
  }

  /**
   * 设置当前驱动协议版本。
   * @param {string|null} version - 例如 V3.0、V3.1.2；null 时使用旧协议基线
   */
  setProtocolVersion(version) {
    this.protocolVersion = version || null;
  }

  /**
   * 当前已完整定义的是 V3.0 及以下布局；未知版本也按该基线解析。
   */
  isLegacyProtocol() {
    if (!this.protocolVersion) return true;
    const [major, minor, patch] = this.getProtocolVersionParts();
    return (
      major < 3 ||
      (major === 3 && minor === 0 && patch === 0)
    );
  }

  /**
   * 将 V4.0、V4.0.1 等显示版本转换为可比较的三段数字。
   */
  getProtocolVersionParts() {
    const match = String(this.protocolVersion || '').match(
      /(\d+)(?:\.(\d+))?(?:\.(\d+))?/
    );
    if (!match) return [0, 0, 0];
    return [
      Number(match[1]),
      Number(match[2] || 0),
      Number(match[3] || 0)
    ];
  }

  isProtocolAtLeast(targetMajor, targetMinor = 0, targetPatch = 0) {
    const current = this.getProtocolVersionParts();
    const target = [targetMajor, targetMinor, targetPatch];
    for (let i = 0; i < target.length; i++) {
      if (current[i] !== target[i]) return current[i] > target[i];
    }
    return true;
  }

  /**
   * 设置设备基本信息
   * @param {Object} info - 设备信息对象
   */
  setDeviceInfo(info) {
    this.data.deviceInfo = info;
    this.emit('deviceInfo', info);
  }

  /**
   * 设置设备配置数据
   * @param {Uint8Array} data - 配置数据
   */
  setDeviceConfig(data) {
    this.data.deviceConfig = {
      raw: data,
      parsed: this.parseDeviceConfig(data),
      timestamp: Date.now()
    };
    this.addToHistory('deviceConfig', data);
    this.emit('deviceConfig', this.data.deviceConfig);
  }

  /**
   * 设置通告信息
   * @param {Uint8Array} data - 通告数据
   */
  setInformationData(data) {
    this.data.informationData = {
      raw: data,
      parsed: this.parseInformationData(data),
      timestamp: Date.now()
    };
    this.addToHistory('informationData', data);
    this.emit('informationData', this.data.informationData);
  }

  /**
   * 设置鼠标按键信息
   * @param {Uint8Array} data - 按键数据
   */
  setMouseButtonData(data) {
    this.data.mouseButtonData = {
      raw: data,
      parsed: this.parseMouseButtonData(data),
      timestamp: Date.now()
    };
    console.log('this.data.mouseButtonData', this.data.mouseButtonData);
    this.addToHistory('mouseButtonData', data);
    this.emit('mouseButtonData', this.data.mouseButtonData);
  }

  /**
   * 写入一包 RT 实时采样。高频推送不进历史，避免撑爆 rawDataHistory。
   * @param {Uint8Array} data
   */
  setRtMonitorSample(data) {
    const parsed = parseRtMonitorSample(data);
    if (!parsed) return null;
    this.data.rtMonitor = {
      raw: data,
      parsed,
      timestamp: Date.now(),
      receivedAt:
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now(),
    };
    this.emit('rtMonitor', this.data.rtMonitor);
    return this.data.rtMonitor;
  }

  clearRtMonitor() {
    this.data.rtMonitor = null;
    this.emit('rtMonitor', null);
  }

  /**
   * 写入一包 RT 档位采样（0xF2）。布局与 0xF1 相同，只是事件通道分开，
   * 避免档位动画和 ADC 曲线互相触发。高频推送同样不进历史。
   * @param {Uint8Array} data
   */
  setRtGearSample(data) {
    const parsed = parseRtGearSample(data);
    if (!parsed) return null;
    this.data.rtGear = {
      raw: data,
      parsed,
      timestamp: Date.now(),
      receivedAt:
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now(),
    };
    this.emit('rtGear', this.data.rtGear);
    return this.data.rtGear;
  }

  clearRtGear() {
    this.data.rtGear = null;
    this.emit('rtGear', null);
  }

  /**
   * 设置设备性能数据
   * @param {Uint8Array} data - 性能数据
   */
  setPerformanceData(data) {
    // 统一保存为 64 字节缓冲，避免 WebHID 短报告导致回写 Fail to write report
    let raw = data instanceof Uint8Array ? data : new Uint8Array(data || []);
    if (raw.length < 64) {
      const padded = new Uint8Array(64);
      padded.set(raw);
      raw = padded;
    } else if (raw.length > 64) {
      raw = raw.slice(0, 64);
    }

    const parsed = this.parsePerformanceData(raw);
    const dataType = raw[3];

    const performanceDataItem = {
      raw,
      parsed: parsed,
      timestamp: Date.now()
    };
    
    // 根据类型分别存储
    if (dataType === 1) {
      this.data.performanceData.type1 = performanceDataItem;
    } else if (dataType === 3) {
      this.data.performanceData.type3 = performanceDataItem;
    }
    
    this.addToHistory('performanceData', data);
    this.emit('performanceData', this.data.performanceData);
  }

  /**
   * 设置颜色数据
   * @param {Uint8Array} data - 颜色数据
   */
  setColorData(data) {
    this.data.colorData = {
      raw: data,
      parsed: this.parseColorData(data),
      timestamp: Date.now()
    };
    this.addToHistory('colorData', data);
    this.emit('colorData', this.data.colorData);
  }

  /**
   * 切换灯效（command=5 / type=5）后同步已存的灯光数据。
   *
   * 设备对这条命令只做一件事：拿包里的 effect 序号替换已存数据中的 effect，
   * 其余字节（各组 effect_info_t、effectSleepTime、档位表）一律不动。缓存也
   * 照做，别整包覆盖成只带一个字节的回包。
   *
   * 除了照常发 colorData，还单独发一个 lightingEffectSwitch 事件：灯光页
   * 在用户动过界面之后就不再拿 colorData 回灌表单（回读比写入慢，会把正在
   * 调的值打回去），但设备自己切了灯效时界面仍要跟上，靠的就是这个事件。
   *
   * @param {number} effect - 新的灯效序号
   * @returns {boolean} 是否更新成功（没有已存灯光数据时返回 false）
   */
  applyLightingEffectSwitch(effect) {
    const next = applyEffectSwitchToBuffer(this.data.colorData?.raw, effect);
    if (!next) {
      console.warn('没有已存的灯光数据，切换灯效回包暂不入库');
      return false;
    }
    this.setColorData(next);
    this.emit('lightingEffectSwitch', {
      effect: Number(effect) & 0xff,
      colorData: this.data.colorData,
    });
    return true;
  }

  /**
   * 设置宏数据
   * @param {Uint8Array} data - 宏数据
   */
  setMacroData(data) {
    this.data.macroData = {
      raw: data,
      parsed: this.parseMacroData(data),
      timestamp: Date.now()
    };
    this.addToHistory('macroData', data);
    this.emit('macroData', this.data.macroData);
  }

  /**
   * 解析设备配置数据
   * @param {Uint8Array} data - 原始数据
   * @returns {Object} 解析后的配置对象
   * 
   * 数据结构：
   * - data[0]: 命令
   * - data[1]: 索引
   * - data[2]: 长度
   * - data[3]: CMD
   * - data[4-7]: 产品ID (0-3)
   * - data[8-9]: 软件版本 BCD (4-5)
   * - data[10-14]: MAC[5] (6-10)
   * - data[15]: Flag (11)
   * - data[16-19]: MCU2产品ID (12-15)
   * - data[20-21]: MCU2软件版本 BCD (16-17)
   * - data[22-26]: MCU2 MAC[5] (18-22)
   * - data[27]: MCU2 Flag (23)
   * - data[28]: WirelessOnConnected (24)
   * - data[29-32]: DeviceID (25-28)
   * - data[33-34]: usbVID (29-30)
   * - data[35-36]: usbPID (31-32)
   * - data[37-38]: tftMcu3Version (33-34)
   * - data[39-40]: mcu4Version (35-36)
   */
  parseDeviceConfig(data: Uint8Array) {
    if (!data || data.length < 41) return null;

    const softwareVersionRaw = this.readUint16LE(data, 8);
    const mcu2SoftwareVersionRaw = this.readUint16LE(data, 20);
    const tftMcu3VersionRaw = this.readUint16LE(data, 37);
    const mcu4VersionRaw = this.readUint16LE(data, 39);

    return {
      // 协议头部（各占1字节）
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      cmd: data[3],
      
      // 配置数据部分
      productId: this.readUint32LE(data, 4),                      // 产品ID (0-3)
      softwareVersion: this.parseBCDVersion(softwareVersionRaw),  // 软件版本 BCD (4-5)
      softwareVersionRaw: softwareVersionRaw,                     // 原始版本号
      mac: Array.from(data.slice(10, 15)),                        // MAC[5] (6-10)
      flag: data[15],                                             // Flag (11)
      mcu2ProductId: this.readUint32LE(data, 16),                 // MCU2产品ID (12-15)
      mcu2SoftwareVersion: this.parseBCDVersion(mcu2SoftwareVersionRaw), // MCU2软件版本 BCD (16-17)
      mcu2SoftwareVersionRaw: mcu2SoftwareVersionRaw,             // MCU2原始版本号
      mcu2Mac: Array.from(data.slice(22, 27)),                    // MCU2 MAC[5] (18-22)
      mcu2Flag: data[27],                                         // MCU2 Flag (23)
      wirelessOnConnected: data[28],                              // WirelessOnConnected (24)
      deviceId: this.readUint32LE(data, 29),                      // DeviceID (25-28)
      usbVID: this.readUint16LE(data, 33),                        // usbVID (29-30)
      usbPID: this.readUint16LE(data, 35),                        // usbPID (31-32)
      tftMcu3Version: this.parseBCDVersion(tftMcu3VersionRaw),    // tftMcu3Version (33-34)
      tftMcu3VersionRaw: tftMcu3VersionRaw,                       // TFT MCU3原始版本号
      mcu4Version: this.parseBCDVersion(mcu4VersionRaw),          // mcu4Version (35-36)
      mcu4VersionRaw: mcu4VersionRaw                              // MCU4原始版本号
    };
  }

  /**
   * 解析通告信息数据
   * @param {Uint8Array} data - 原始数据
   * @returns {Object} 解析后的通告信息
   */
  parseInformationData(data) {
    if (this.isProtocolAtLeast(4, 0)) {
      return this.parseV4InformationData(data);
    }

    if (!data || data.length < 27) return null;

    console.log(`rawDeviceVIDPID: ${this.toHexString(data.slice(6, 10))}`);
    const deviceVIDPID = this.readUint32LE(data, 6);
    // Device VIDPID: 前16位(高位)为PID，后16位(低位)为VID
    const devicePID = (deviceVIDPID >> 16) & 0xFFFF;
    const deviceVID = deviceVIDPID & 0xFFFF;


    console.log(`deviceVIPPID: ${deviceVIDPID}`);
    
    const reportRateLevel = data[20];
    const reportRateHz = this.reportRateLevelToHz(reportRateLevel);
    
    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      deviceState: data[4],
      reportRateMax: data[5],
      deviceVIDPID: deviceVIDPID,  // 完整的32位值
      devicePID: devicePID,         // 高16位 - PID
      deviceVID: deviceVID,         // 低16位 - VID
      chargingFlag: data[10],
      batteryLevel: data[11],
      profileIndex: data[12],
      dpiIndex: data[13],
      dpiX: this.readUint16LE(data, 14),
      dpiY: this.readUint16LE(data, 16),
      sensorFlag: data[18],
      nc: data[19],
      reportRate: reportRateLevel,      // 回报率等级 (0-6)
      reportRateHz: reportRateHz,       // 回报率频率 (Hz)
      lightingMode: data[21],
      lightingColor: data[22],
      lightingSpeed: data[23],
      lightingBrightness: data[24],
      lightingDirection: data[25],
      lightingFade: data[26],
      reportRateStep: data[27]
    };
  }

  /**
   * V4.0 command=1 通告信息。
   *
   *  4     deviceState
   *  5     reportRateMax
   *  6-7   deviceVID
   *  8-9   devicePID
   * 10     chargingFlag
   * 11     batLevel
   * 12     updateFlag
   * 13     profile
   * 14     sensorType（0=3950, 1=3955, 2=3395）
   * 15     sensorNC
   * 16     sensorFlag（Bit0-4 = LOD）
   * 17     dpiLevel
   * 18-19  dpiX
   * 20-21  dpiY
   * 22-24  dpiRGB
   * 25     reportRateLevel
   * 26     reportRate
   * 27-29  reportRateRGB
   * 30-35  LightingEffect / Color / Speed / Brightness / Direction / Fade
   */
  parseV4InformationData(data) {
    if (!data || data.length < 36) return null;

    const deviceVIDBytes = Array.from(data.slice(6, 8));
    const devicePIDBytes = Array.from(data.slice(8, 10));
    const sensorType = data[14];
    const sensorNC = data[15];
    const sensorFlag = data[16];
    const dpiRGB = Array.from(data.slice(22, 25));
    const reportRateRGB = Array.from(data.slice(27, 30));
    const reportRateLevel = data[25];
    const lightingEffect = data[30];
    const lightingColor = data[31];
    const lightingSpeed = data[32];
    const lightingBrightness = data[33];
    const lightingDirection = data[34];
    const lightingFade = data[35];

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      deviceState: data[4],
      reportRateMax: data[5],
      deviceVIDBytes,
      devicePIDBytes,
      deviceVID: this.readUint16LE(data, 6),
      devicePID: this.readUint16LE(data, 8),
      chargingFlag: data[10],
      batLevel: data[11],
      batteryLevel: data[11],
      updateFlag: data[12],
      profile: data[13],
      profileIndex: data[13],
      sensorType,
      sensorNC,
      sensorFlag,
      sensorLod: sensorFlag & 0x1f,
      dpiLevel: data[17],
      dpiIndex: data[17],
      dpiX: this.readUint16LE(data, 18),
      dpiY: this.readUint16LE(data, 20),
      dpiRGB,
      reportRateLevel,
      reportRate: data[26],
      reportRateHz: this.reportRateLevelToHz(reportRateLevel),
      reportRateRGB,
      lightingEffect,
      lightingMode: lightingEffect,
      lightingColor,
      lightingSpeed,
      lightingBrightness,
      lightingDirection,
      lightingFade,
      // 同时保留协议文档中的字段大小写，便于逐字段核对原始定义。
      LightingEffect: lightingEffect,
      LightingColor: lightingColor,
      LightingSpeed: lightingSpeed,
      LightingBrightness: lightingBrightness,
      LightingDirection: lightingDirection,
      LightingFade: lightingFade
    };
  }

  /**
   * 解析鼠标按键数据
   * @param {Uint8Array} data - 原始数据
   * @returns {Object} 解析后的按键信息
   */
  parseMouseButtonData(data) {
    if (!data || data.length < 52) return null;

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      leftButton: this.toHex32LE(data, 4),
      rightButton: this.toHex32LE(data, 8),
      middleButton: this.toHex32LE(data, 12),
      button4: this.toHex32LE(data, 16),
      button5: this.toHex32LE(data, 20),
      dpiButton: this.toHex32LE(data, 24),
      reportRateButton: this.toHex32LE(data, 28),
      pairButton: this.toHex32LE(data, 32),
      nc1: this.toHex32LE(data, 36),
      nc2: this.toHex32LE(data, 40),
      scrollUp: this.toHex32LE(data, 44),
      scrollDown: this.toHex32LE(data, 48)
    };
  }

  /**
   * 解析性能数据
   * @param {Uint8Array} data - 原始数据
   * @returns {Object} 解析后的性能数据
   */
  parsePerformanceData(data) {
    if (!data || data.length < 4) return null;

    const dataType = data[3];
    
    if (dataType === 1) {
      if (this.isProtocolAtLeast(4, 0)) {
        return this.parseV4PerformanceType1(data);
      }

      if (this.isLegacyProtocol()) {
        return this.parseLegacyPerformanceType1(data);
      }

      if (data.length < 15) return null;

      console.log('性能数据类型1长度:', data.length)

      // 动态计算 DPI 档位数量（使用 length 低 7 位作为有效 payload 长度）
      // 字节 0-3: 命令头部, 字节 4-14: 设置字段(11字节), 字节 15+: DPI 数据(每档 4 + 3 RGB = 7字节)
      const dpiDataOffset = 15;
      const bytesPerLevel = 7;  // 4 (DPI X/Y) + 3 (RGB)

      let payloadLength = data[2] & 0x7F; // byte4 起算的长度
      if (!payloadLength) {
        // 兜底：从后往前找最后一个非零字节，推算长度
        for (let i = data.length - 1; i >= 4; i--) {
          if (data[i] !== 0) {
            payloadLength = i - 3;
            break;
          }
        }
      }

      // 有效数据结束位置（不包含）
      const validEnd = Math.min(data.length, 4 + payloadLength);
      const dpiDataLength = Math.max(0, validEnd - dpiDataOffset);
      const dpiLevelCount = Math.max(0, Math.min(7, Math.floor(dpiDataLength / bytesPerLevel)));

      console.log('DPI 档位数量:', dpiLevelCount);

      // 解析 DPI 表格 (从字节 15 开始，每档位 4 字节)
      const dpiTable = [];
      for (let i = 0; i < dpiLevelCount; i++) {
        const offset = dpiDataOffset + i * 4;
        dpiTable.push({
          x: this.readUint16LE(data, offset),
          y: this.readUint16LE(data, offset + 2)
        });
      }

      // 解析 DPI 灯颜色 (紧跟在 DPI 表格后面，每档位 3 字节)
      const dpiRGBTableOffset = dpiDataOffset + dpiLevelCount * 4;
      const dpiRGBTable = [];
      for (let i = 0; i < dpiLevelCount; i++) {
        const offset = dpiRGBTableOffset + i * 3;
        dpiRGBTable.push([
          data[offset],     // R
          data[offset + 1], // G
          data[offset + 2]  // B
        ]);
      }

      return {
        command: parseCommandByte(data[0]).command,
        index: data[1],
        length: data[2],
        type: dataType,
        angleSnapEnable: data[4],
        rippleEnable: data[5],
        motionSyncEnable: data[6],
        fullSpeedEnable: data[7],
        fullbloodEnable: data[8],
        liftoffButtonEnable: data[9],
        glassModeDiable: data[10],
        liftoffHeight: data[11],                  // 抬起距离
        angleTunability: this.readInt8(data, 12), // 角度偏移（int8，-90~90）
        dpiCurrentLevel: data[13],    // 当前DPI档位
        dpiflag: data[14],            // DPI标志
        dpiLevelCount: dpiLevelCount, // DPI 档位数量
        dpiTable: dpiTable,           // DPI 档位表
        dpiRGBTable,                  // DPI 灯颜色
      };
    } else if (dataType === 3) {
      if (this.isProtocolAtLeast(4, 0)) {
        return this.parseV4PerformanceType3(data);
      }

      // 驱动协议 4.0 以下的 type3 只有一个 reportRate 字段，
      // 同时控制有线和无线；未知版本也按该旧协议布局保护性解析。
      return this.parseLegacyPerformanceType3(data);
    }

    return { raw: Array.from(data) };
  }

  /**
   * V4.0 command=4/type=1。
   * 协议字段编号是 byte3 之后的 payload 偏移，因此实际包偏移统一加 4。
   */
  parseV4PerformanceType1(data) {
    if (data.length < 59) return null;

    const dpiTable = [];
    const dpiTableBytes = [];
    const dpiRGBTable = [];
    for (let i = 0; i < 6; i++) {
      const dpiOffset = 15 + i * 4;
      const dpiBytes = Array.from(data.slice(dpiOffset, dpiOffset + 4));
      dpiTableBytes.push(dpiBytes);
      dpiTable.push({
        x: this.readUint16LE(data, dpiOffset),
        y: this.readUint16LE(data, dpiOffset + 2)
      });

      const rgbOffset = 39 + i * 3;
      dpiRGBTable.push(Array.from(data.slice(rgbOffset, rgbOffset + 3)));
    }

    const angleSnapEnable = data[4];
    const rippleEnable = data[5];
    const motionSyncEnable = data[6];
    const fullSpeedEnable = data[7];
    const fullbloodEnable = data[8];
    const liftoffButtonEnable = data[9];
    const glassModeDiable = data[10];
    const liftoffHeight = data[11];
    const dpiflag = data[14];

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      angleSnapEnable,
      rippleEnable,
      motionSyncEnable,
      fullSpeedEnable,
      fullbloodEnable,
      liftoffButtonEnable,
      glassModeDiable,
      liftoffHeight,
      angleTunability: this.readInt8(data, 12),
      dpiCurrentLevel: data[13],
      dpiflag,
      dpiLevelCount: 6,
      dpiTable,
      dpiTableBytes,
      dpiRGBTable,
      pYEnable: data[57],
      pYOffset: (data[58] << 24) >> 24
    };
  }

  /**
   * V4.0 command=4/type=3。
   * 协议字段编号是从 byte4 开始的 payload 偏移，实际包偏移统一加 4。
   */
  parseV4PerformanceType3(data) {
    if (data.length < V4_TYPE3_MIN_LENGTH) return null;

    const coredRPTTABLE = Array.from(data.slice(10, 17));
    const wirelessRPTTABLE = Array.from(data.slice(19, 26));
    // byte27 起的按键性能字段偏移集中在 ButtonPerformance，写包与解析共用同一份
    const offsets = V4_TYPE3_OFFSETS;
    const hasCustomRates = data.length >= V4_TYPE3_CUSTOM_RATE_MIN_LENGTH;
    const coredRpt = hasCustomRates
      ? this.readUint16LE(data, offsets.coredRpt)
      : 0;
    const wireLessRpt = hasCustomRates
      ? this.readUint16LE(data, offsets.wireLessRpt)
      : 0;
    const coredRateBits = decodeReportRateByte(data[8]);
    const wirelessRateBits = decodeReportRateByte(data[17]);

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      SysSleepTime: this.readUint16LE(data, 4),
      bleSysSleepTime: this.readUint16LE(data, 6),
      coredReportRate: data[8],
      coredReportRateLevel: coredRateBits.level,
      coredReportRateCustom: coredRateBits.custom,
      coredRpt,
      coredReportRateHz: resolveReportRateHz(data[8], coredRpt),
      coredRPTFlag: data[9],
      coredRPTTABLE,
      coredSupportedReportRates: pollingRateTableToHz(coredRPTTABLE),
      wirelessReportRate: data[17],
      wirelessReportRateLevel: wirelessRateBits.level,
      wirelessReportRateCustom: wirelessRateBits.custom,
      wireLessRpt,
      wirelessReportRateHz: resolveReportRateHz(data[17], wireLessRpt),
      wirelessRPTFlag: data[18],
      wirelessRPTTABLE,
      wirelessSupportedReportRates: pollingRateTableToHz(wirelessRPTTABLE),
      keyScanRate: data[26],
      buttonType: data[offsets.buttonType],
      leftButtonMode: data[offsets.leftButtonMode],
      rightButtonMode: data[offsets.rightButtonMode],
      fastButtonEnable: data[offsets.fastButtonEnable],
      leftDebouncetimePress: data[offsets.leftDebouncetimePress],
      leftDebouncetimeFree: data[offsets.leftDebouncetimeFree],
      rightDebouncetimePress: data[offsets.rightDebouncetimePress],
      rightDebouncetimeFree: data[offsets.rightDebouncetimeFree],
      otherDebouncetimePress: data[offsets.otherDebouncetimePress],
      otherDebouncetimeFree: data[offsets.otherDebouncetimeFree],
      leftButtonRTPress: data[offsets.leftButtonRTPress],
      leftButtonRTFree: data[offsets.leftButtonRTFree],
      leftMotorType: data[offsets.leftMotorType],
      leftMotorLevel: data[offsets.leftMotorLevel],
      rightButtonRTPress: data[offsets.rightButtonRTPress],
      rightButtonRTFree: data[offsets.rightButtonRTFree],
      rightMotorType: data[offsets.rightMotorType],
      rightMotorLevel: data[offsets.rightMotorLevel],
      buttonWakeupEnable: data[offsets.buttonWakeupEnable],
      moveWakeupEnable: data[offsets.moveWakeupEnable],
      wheelWakeupEnable: data[offsets.wheelWakeupEnable],
      wheelReverseEnable: data[offsets.wheelReverseEnable],
      wheelDelayEnable: data[offsets.wheelDelayEnable],
      wheelDelayTimeStart: this.readUint16LE(data, offsets.wheelDelayTimeStart),
      wheelDelayTimeEnd: this.readUint16LE(data, offsets.wheelDelayTimeEnd),
      rfPowerMode: data[offsets.rfPowerMode],
      bleNum: data[offsets.bleNum]
    };
  }

  /**
   * V3.0 及以下：协议字段编号是从 byte4 开始的 payload 偏移。
   * payload 11-34 的 DPI 表对应完整包 byte15-38；
   * payload 35-52 的 RGB 表对应完整包 byte39-56。
   */
  parseLegacyPerformanceType1(data) {
    if (data.length < 57) return null;

    const dpiTable = [];
    const dpiRGBTable = [];
    for (let i = 0; i < 6; i++) {
      const dpiOffset = 15 + i * 4;
      dpiTable.push({
        x: this.readUint16LE(data, dpiOffset),
        y: this.readUint16LE(data, dpiOffset + 2)
      });

      const rgbOffset = 39 + i * 3;
      dpiRGBTable.push([
        data[rgbOffset],
        data[rgbOffset + 1],
        data[rgbOffset + 2]
      ]);
    }

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      angleSnapEnable: data[4],
      rippleEnable: data[5],
      motionSyncEnable: data[6],
      fullSpeedEnable: data[7],
      fullbloodEnable: data[8],
      liftoffButtonEnable: data[9],
      glassModeDiable: data[10],
      liftoffHeight: data[11],
      angleTunability: this.readInt8(data, 12),
      dpiCurrentLevel: data[13],
      dpiflag: data[14],
      dpiLevelCount: 6,
      dpiTable,
      dpiRGBTable
    };
  }

  /**
   * V4.0 以下 command=4/type=3：payload 固定 16 字节。
   * 协议表中的字段编号是 payload 偏移，完整包下标统一加 4。
   * payload byte4 的 reportRate 同时控制有线和无线，协议不包含回报率表，
   * 因此可选档位只能用 LEGACY_SUPPORTED_REPORT_RATES_HZ 兜底。
   */
  parseLegacyPerformanceType3(data) {
    const payloadOffset = 4;
    const packetOffset = (fieldOffset) => payloadOffset + fieldOffset;
    const payloadLength = 16;
    if (data.length < payloadOffset + payloadLength) return null;

    const reportRate = data[packetOffset(4)];
    const supportedReportRates = [...LEGACY_SUPPORTED_REPORT_RATES_HZ];

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      SysSleepTime: this.readUint16LE(data, packetOffset(0)),
      bleSysSleepTime: this.readUint16LE(data, packetOffset(2)),
      reportRate,
      // 保留两侧别名供现有界面使用；旧协议实际只有同一个字段。
      coredReportRate: reportRate,
      coredReportRateLevel: reportRate,
      coredReportRateCustom: false,
      coredRpt: 0,
      coredReportRateHz: pollingRateLevelToHz(reportRate),
      wirelessReportRate: reportRate,
      wirelessReportRateLevel: reportRate,
      wirelessReportRateCustom: false,
      wireLessRpt: 0,
      wirelessReportRateHz: pollingRateLevelToHz(reportRate),
      coredSupportedReportRates: supportedReportRates,
      wirelessSupportedReportRates: [...supportedReportRates],
      buttonAntiShakeTime: data[packetOffset(5)],
      leftDebouncetimePress: data[packetOffset(5)],
      fastButtonEnable: data[packetOffset(6)],
      buttonWakeupEnable: data[packetOffset(7)],
      moveWakeupEnable: data[packetOffset(8)],
      wheelWakeupEnable: data[packetOffset(9)],
      wheelReverseEnable: data[packetOffset(10)],
      irButtonMode: data[packetOffset(11)],
      leftButtonIREnable: data[packetOffset(12)],
      RightButtonIREnable: data[packetOffset(13)],
      rfPowerMode: data[packetOffset(14)],
      bleNum: data[packetOffset(15)]
    };
  }

  /**
   * 解析颜色数据
   * @param {Uint8Array} data - 原始数据
   * @returns {Object} 解析后的颜色数据
   */
  parseColorData(data) {
    if (!data || data.length < 7) return null;

    const parsed = {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      effectSleepTime: this.readUint16LE(data, 4),
      effect: data[6],
      rawData: Array.from(data.slice(7))
    };

    // V4.0 以下的 M9/M6 Ultra：command=5/type=1 的 byte39-56
    // 是 uint8_t dpiRGBTable[6][3]，按 6 档 × RGB 三字节拆分。
    if (data[3] === 1 && data.length >= 57) {
      const dpiRGBTable = [];
      for (let i = 0; i < 6; i++) {
        const offset = 39 + i * 3;
        dpiRGBTable.push(Array.from(data.slice(offset, offset + 3)));
      }
      parsed.dpiRGBTable = dpiRGBTable;
    }

    // 带灯机型的 command=5/type=1 是完整灯效结构（effectSleepTime + effect +
    // effectInfo[]）。附加解析，旧的 dpiRGBTable 视图保持不变。
    parsed.lighting = parseLightingData(data);

    return parsed;
  }

  /**
   * 解析宏数据（支持多包合并后的数据）
   * @param {Uint8Array} data - 原始数据（可能是合并后的多包数据）
   * @returns {Object} 解析后的宏数据
   *
   * 数据结构：
   * - data[0]: 命令 (0x07)
   * - data[1]: 包索引 (合并后为0)
   * - data[2]: 长度字段
   * - data[3]: 类型
   * - data[4-5]: 数据总长度（2字节小端）
   * - data[6+]: 宏数据内容
   */
  parseMacroData(data) {
    if (!data || data.length < 6) return null;

    // 设备以 0xFFFF 作为“没有板载宏”的哨兵值，而不是实际数据长度。
    if (data[4] === 0xFF && data[5] === 0xFF) {
      console.log('设备宏为空');
      return {
        command: parseCommandByte(data[0]).command,
        index: data[1],
        length: data[2],
        type: data[3],
        dataLength: 0,
        macroCount: 0,
        macroObjects: [],
        isEmpty: true,
        rawData: Array.from(data.slice(4)),
        totalBytes: data.length
      };
    }

    const dataLength = this.readUint16LE(data, 4);  // 总数据长度（2字节小端）
    console.log(`宏总长度: ${dataLength}`);

    // 解析宏对象列表
    const macroObjects = [];
    let currentIndex = 6;  // 从索引6开始（跳过头部4字节 + 长度2字节）

    while (currentIndex < data.length - 1) {
      // 读取对象长度（2字节，小端序）
      const objLength = this.readUint16LE(data, currentIndex);

      if (objLength === 0 || currentIndex + objLength > data.length) {
        break;
      }

      // 提取对象数据（包含长度字段）
      const objData = data.slice(currentIndex, currentIndex + objLength);

      // 解析宏对象字段
      // 0-1: 长度, 2-3: 次数, 4: 序号, 5: 类型
      // 6-39: 宏名字 (34字节), 40-41: 按键延时, 42-43: 事件个数
      // 44-N: 事件数据 (每个事件4字节)
      const loopCount = this.readUint16LE(objData, 2);    // 次数
      const macroIndex = objData[4];                       // 序号
      const macroType = objData[5];                        // 类型

      // 提取宏名字 (6-39, 34字节, UTF-8编码, 去除尾部的0)
      const nameBytes = objData.slice(6, 40);
      let endIndex = nameBytes.indexOf(0);
      if (endIndex === -1) endIndex = nameBytes.length;
      const macroName = new TextDecoder('utf-8').decode(nameBytes.slice(0, endIndex)) || `宏 ${macroIndex + 1}`;

      const delayMode = this.readUint16LE(objData, 40);     // 延时模式: 0=单独延时, 1+=统一延时(ms)
      const eventCount = this.readUint16LE(objData, 42);   // 事件个数

      // 解析事件数据 (从字节44开始, 每个事件4字节)
      // 格式: [动作|类型][值][延迟低][延迟高]
      // Byte0: bit7=动作(1抬起,0按下), 低7位=类型
      // Byte1: 操作值 (键码/按钮/数值)
      // Byte2-3: 延迟 (小端序, ms)
      // 类型: 1键盘,2修饰键,3多媒体,4鼠标按键,5滚轮,6鼠标X,7鼠标Y,8鼠标移动
      // 类型4 的 Byte1 是按键位掩码；类型5/6/7 的 Byte1 是有符号字节(-127~127)
      // 类型8（协议 V3.0 新增）没有延时字段：Byte1-3 全部用于 X/Y 位移，固定 5ms 延时
      const EVENT_TYPES = MACRO_EVENT_TYPE_NAMES;

      const events = [];
      for (let i = 0; i < eventCount; i++) {
        const eventOffset = 44 + i * 4;
        if (eventOffset + 4 <= objData.length) {
          const byte0 = objData[eventOffset];
          const byte1 = objData[eventOffset + 1];
          const byte2 = objData[eventOffset + 2];
          const byte3 = objData[eventOffset + 3];

          const isKeyUp = (byte0 & 0x80) !== 0;  // bit7=1 表示抬起
          const eventType = byte0 & 0x7F;        // 低7位是类型
          const isMouseMove = eventType === MACRO_EVENT_TYPE_MOUSE_MOVE;

          const event = {
            action: isMouseMove ? 'move' : (isKeyUp ? 'up' : 'down'),
            type: eventType,                      // 类型: 1-8
            typeName: EVENT_TYPES[eventType] || `未知(${eventType})`,
            value: byte1,                         // byte1是操作值
            // 鼠标移动没有延时字段，设备固定 5ms
            delay: isMouseMove ? MACRO_MOUSE_MOVE_DELAY_MS : ((byte3 << 8) | byte2),
            raw: Array.from(objData.slice(eventOffset, eventOffset + 4))
          };

          if (isMouseMove) {
            const { x, y } = decodeMacroMouseMove(byte1, byte2, byte3);
            event.moveX = x;
            event.moveY = y;
          } else if (isMacroAxisEventType(eventType)) {
            // 滚轮 / 鼠标X / 鼠标Y：Byte1 是补码，单独给出有符号值供 UI 展示
            event.axisValue = decodeMacroAxisValue(byte1);
          }

          events.push(event);
        }
      }

      macroObjects.push({
        startIndex: currentIndex - 4,
        length: objLength,
        loopCount: loopCount,
        index: macroIndex,
        type: macroType,
        name: macroName,
        delayMode: delayMode,  // 0=单独延时, 1+=统一延时(ms)
        eventCount: eventCount,
        events: events,
        rawData: Array.from(objData)
      });

      const delayModeStr = delayMode === 0 ? '单独延时' : `统一延时${delayMode}ms`;
      console.log(`解析宏对象 ${macroObjects.length}: "${macroName}", 序号=${macroIndex}, 类型=${macroType}, 次数=${loopCount}, ${delayModeStr}, 事件数=${eventCount}`);

      // 打印事件数据 (44-N)
      if (events.length > 0) {
        console.log(`  事件数据 (44-${44 + eventCount * 4 - 1}):`);
        events.forEach((event, i) => {
          const hexStr = event.raw.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
          if (event.type === MACRO_EVENT_TYPE_MOUSE_MOVE) {
            console.log(`    事件${i + 1}: [${hexStr}] ✥ 类型=${event.typeName} X=${event.moveX} Y=${event.moveY} 延迟=${event.delay}ms(固定)`);
            return;
          }
          const actionStr = event.action === 'up' ? '↑' : '↓';
          console.log(`    事件${i + 1}: [${hexStr}] ${actionStr} 类型=${event.typeName} 值=0x${event.value.toString(16).padStart(2, '0')} 延迟=${event.delay}ms`);
        });
      }

      // 移动到下一个对象
      currentIndex += objLength;
    }

    console.log(`共解析出 ${macroObjects.length} 个宏对象`);

    return {
      command: parseCommandByte(data[0]).command,
      index: data[1],
      length: data[2],
      type: data[3],
      dataLength: dataLength,           // 数据总长度
      macroCount: macroObjects.length,  // 宏对象数量
      macroObjects: macroObjects,       // 宏对象列表
      isEmpty: false,
      rawData: Array.from(data.slice(4)),      // 从第4字节开始的所有原始数据
      totalBytes: data.length            // 总字节数
    };
  }

  /**
   * 读取小端序 Uint16
   */
  readUint16LE(data, offset) {
    return (data[offset + 1] << 8) | data[offset];
  }

  /**
   * 读取有符号 Int8（角度偏移等带正负的字段）
   */
  readInt8(data, offset) {
    return (data[offset] << 24) >> 24;
  }

  /**
   * 读取小端序 Uint32
   */
  readUint32LE(data, offset) {
    return ((data[offset + 3] << 24) | 
            (data[offset + 2] << 16) | 
            (data[offset + 1] << 8) | 
            data[offset]) >>> 0;
  }
  /**
   * 解析 BCD 格式的版本号
   * @param {number} version - Uint16 格式的版本号
   * @returns {string} 格式化的版本字符串 (major.minor.patch)
   * 
   * 格式说明 (0xMMNP)：
   * - MM (高字节，2位十六进制) = 大版本
   * - N (低字节高4位，1位十六进制) = 次版本
   * - P (低字节低4位，1位十六进制) = 修订版本
   * 
   * 示例：
   * - 0x0110 -> "1.1.0"
   * - 0x0123 -> "1.2.3"
   * - 0x0A45 -> "10.4.5"
   */
  parseBCDVersion(version) {
    const major = (version >> 8) & 0xFF;  // 高字节：大版本
    const minor = (version >> 4) & 0x0F;  // 低字节高4位：次版本
    const patch = version & 0x0F;         // 低字节低4位：修订版本
    return `${major}.${minor}.${patch}`;
  }

  /**
   * 将回报率等级转换为频率(Hz)
   * @param {number} level - 回报率等级 (0-6)
   * @returns {number} 回报率频率(Hz)
   * 
   * 等级映射：
   * - 0: 125Hz (8ms)
   * - 1: 250Hz (4ms)
   * - 2: 500Hz (2ms)
   * - 3: 1000Hz (1ms, 1KHz)
   * - 4: 2000Hz (500us, 2KHz)
   * - 5: 4000Hz (250us, 4KHz)
   * - 6: 8000Hz (125us, 8KHz)
   */
  reportRateLevelToHz(level) {
    return pollingRateLevelToHz(level) || 1000; // 默认返回1000Hz
  }

  /**
   * 获取所有数据
   * @returns {Object} 所有存储的数据
   */
  getAllData() {
    return { ...this.data };
  }

  /**
   * 获取特定类型的数据
   * @param {string} type - 数据类型
   * @returns {*} 对应的数据
   */
  getData(type) {
    return this.data[type];
  }

  /**
   * 添加到历史记录
   * @param {string} type - 数据类型
   * @param {Uint8Array} data - 原始数据
   */
  addToHistory(type, data) {
    this.data.rawDataHistory.push({
      type,
      data: Array.from(data),
      timestamp: Date.now()
    });

    // 限制历史记录大小
    if (this.data.rawDataHistory.length > this.maxHistorySize) {
      this.data.rawDataHistory.shift();
    }
  }

  /**
   * 获取历史记录
   * @param {number} count - 获取数量
   * @returns {Array} 历史记录数组
   */
  getHistory(count = 10) {
    return this.data.rawDataHistory.slice(-count);
  }

  /**
   * 注册数据变化监听器
   * @param {string} eventType - 事件类型
   * @param {Function} callback - 回调函数
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  /**
   * 移除监听器
   * @param {string} eventType - 事件类型
   * @param {Function} callback - 回调函数
   */
  off(eventType, callback) {
    if (!this.listeners.has(eventType)) return;
    
    const callbacks = this.listeners.get(eventType);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * 触发事件
   * @param {string} eventType - 事件类型
   * @param {*} data - 事件数据
   */
  emit(eventType, data) {
    if (!this.listeners.has(eventType)) return;
    
    this.listeners.get(eventType).forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${eventType} listener:`, error);
      }
    });
  }

  /**
   * 清空所有数据
   */
  clear() {
    this.protocolVersion = null;
    this.data = {
      deviceInfo: null,
      deviceConfig: null,
      informationData: null,
      mouseButtonData: null,
      performanceData: {
        type1: null,
        type3: null
      },
      colorData: null,
      macroData: null,
      rtMonitor: null,
      rtGear: null,
      rawDataHistory: []
    };
    this.emit('clear', null);
  }

  /**
   * 转换为十六进制字符串（按字节分隔）
   * @param {Uint8Array} data - 数据
   * @returns {string} 十六进制字符串
   */
  toHexString(data) {
    return Array.from(data)
      .map(byte => '0x' + byte.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
  }

  /**
   * 将4字节数据按大端序转换为32位16进制字符串
   * @param {Uint8Array} data - 数据
   * @param {number} offset - 起始偏移
   * @returns {string} 16进制字符串 (如: 0x0100F000)
   */
  toHex32LE(data, offset) {
    // 按字节顺序直接拼接（大端序）
    console.log('data', data);
    const hex = Array.from(data.slice(offset, offset + 4))
      .map(byte => byte.toString(16).padStart(2, '0').toUpperCase())
      .join('');
    return '0x' + hex;
  }

  /**
   * 写入16位小端序数值
   * @param {Uint8Array} data - 数据缓冲区
   * @param {number} offset - 写入偏移量
   * @param {number} value - 要写入的值
   */
  writeUint16LE(data, offset, value) {
    data[offset] = value & 0xFF;
    data[offset + 1] = (value >> 8) & 0xFF;
  }

  /**
   * 将解析后的宏对象转换回原始字节数据
   * @param {Object} macroObject - 解析后的宏对象
   * @returns {Uint8Array} 原始字节数据
   */
  buildMacroObjectData(macroObject) {
    const { loopCount, index, type, name, delayMode, events } = macroObject;

    // 计算对象长度: 44字节头部 + 每事件4字节
    const objLength = 44 + events.length * 4;
    const objData = new Uint8Array(objLength);

    // [0-1] 对象长度（小端序）
    this.writeUint16LE(objData, 0, objLength);

    // [2-3] 循环次数（小端序）
    this.writeUint16LE(objData, 2, loopCount);

    // [4] 宏序号
    objData[4] = index;

    // [5] 宏类型
    objData[5] = type;

    // [6-39] 宏名称（34字节，UTF-8编码）
    const nameBytes = new TextEncoder().encode(name);
    for (let i = 0; i < Math.min(nameBytes.length, 34); i++) {
      objData[6 + i] = nameBytes[i];
    }

    // [40-41] 延时模式（小端序）
    this.writeUint16LE(objData, 40, delayMode);

    // [42-43] 事件个数（小端序）
    this.writeUint16LE(objData, 42, events.length);

    // [44+] 事件数据（每个4字节）
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const offset = 44 + i * 4;

      // 鼠标移动（类型8）：byte1-3 全部是坐标，没有延时字段（设备固定 5ms）
      if (event.type === MACRO_EVENT_TYPE_MOUSE_MOVE) {
        const [byte1, byte2, byte3] = encodeMacroMouseMove(event.moveX, event.moveY);
        objData[offset] = MACRO_EVENT_TYPE_MOUSE_MOVE;
        objData[offset + 1] = byte1;
        objData[offset + 2] = byte2;
        objData[offset + 3] = byte3;
        continue;
      }

      // byte0: bit7=动作(1=up,0=down), 低7位=类型
      objData[offset] = (event.action === 'up' ? 0x80 : 0x00) | (event.type & 0x7F);

      // byte1: 操作值（滚轮/鼠标X/鼠标Y 为负值时按补码写入）
      objData[offset + 1] = event.value & 0xff;

      // byte2-3: 延迟（小端序）
      this.writeUint16LE(objData, offset + 2, event.delay);
    }

    return objData;
  }

  /**
   * 构建完整的宏数据用于发送（包含总长度头部）
   * @param {Array} macroObjects - 宏对象数组
   * @returns {Uint8Array} 完整的宏数据（总长度 + 宏对象数据）
   */
  buildMacroData(macroObjects) {
    // 构建所有宏对象的数据
    const objectDataList = macroObjects.map(obj => this.buildMacroObjectData(obj));

    // 计算宏对象数据总长度
    const objectsLength = objectDataList.reduce((sum, data) => sum + data.length, 0);

    // 构建完整数据：[总长度2字节] + [宏对象数据]
    const fullData = new Uint8Array(2 + objectsLength);

    // [0-1] 总长度（小端序）= 宏对象长度 + 2（包含总长度字段自身）
    this.writeUint16LE(fullData, 0, objectsLength + 2);

    // [2+] 宏对象数据
    let offset = 2;
    for (const objData of objectDataList) {
      fullData.set(objData, offset);
      offset += objData.length;
    }

    console.log(`构建宏数据完成: ${fullData.length} 字节, 包含 ${macroObjects.length} 个宏对象`);
    return fullData;
  }

  /**
   * 清理资源
   */
  destroy() {
    this.listeners.clear();
    this.clear();
  }
}

export default DeviceDataStore;
